/**
 * 与 {@code glyphicai.ai.platforms} / {@code platform-image-models.platform-key} 对齐的平台标识。
 * 后端 {@code platform} 路由见 {@link AiPlatformStrategyFactory}（Java）。
 */
export const AI_PLATFORM_DUOMI = 'duomi'

export const AI_PLATFORM_NOWCODING = 'nowcoding'

export const AI_PLATFORM_SHIYUNAPI = 'shiyunapi'

export const AI_PLATFORM_LINGKEAGI = 'lingkeagi'

export const AI_PLATFORM_SILICONFLOW = 'siliconflow'

export const AI_PLATFORM_PIDOI = 'pidoi'

export const AI_PLATFORMS = {
  DUOMI: AI_PLATFORM_DUOMI,
  NOWCODING: AI_PLATFORM_NOWCODING,
  SHIYUNAPI: AI_PLATFORM_SHIYUNAPI,
  LINGKEAGI: AI_PLATFORM_LINGKEAGI,
  SILICONFLOW: AI_PLATFORM_SILICONFLOW,
  PIDOI: AI_PLATFORM_PIDOI,
} as const

export type AIPlatformId = (typeof AI_PLATFORMS)[keyof typeof AI_PLATFORMS]

/** OpenAI {@code /v1/images/generations} 兼容生图平台（诗云：format/quality 等） */
const OPENAI_COMPATIBLE_IMAGE_PLATFORMS = new Set<AIPlatformId>([AI_PLATFORM_SHIYUNAPI])

const PLATFORM_BY_LOWER: Record<string, AIPlatformId> = {
  duomi: AI_PLATFORM_DUOMI,
  nowcoding: AI_PLATFORM_NOWCODING,
  shiyunapi: AI_PLATFORM_SHIYUNAPI,
  lingkeagi: AI_PLATFORM_LINGKEAGI,
  siliconflow: AI_PLATFORM_SILICONFLOW,
  pidoi: AI_PLATFORM_PIDOI,
  /** 展示/历史 platform-tag，与 platform-key 不一致时的别名 */
  shiyun: AI_PLATFORM_SHIYUNAPI,
  lk888: AI_PLATFORM_LINGKEAGI,
  silicon_flow: AI_PLATFORM_SILICONFLOW,
  qigi: AI_PLATFORM_PIDOI,
  qiqi: AI_PLATFORM_PIDOI,
}

/** 展示用 platform-tag → platform-key */
const PLATFORM_TAG_ALIASES: Record<string, AIPlatformId> = {
  duomi: AI_PLATFORM_DUOMI,
  nowcoding: AI_PLATFORM_NOWCODING,
  shiyun: AI_PLATFORM_SHIYUNAPI,
  shiyunapi: AI_PLATFORM_SHIYUNAPI,
  lingke: AI_PLATFORM_LINGKEAGI,
  lingkeagi: AI_PLATFORM_LINGKEAGI,
  lk888: AI_PLATFORM_LINGKEAGI,
  siliconflow: AI_PLATFORM_SILICONFLOW,
  siliconflowcn: AI_PLATFORM_SILICONFLOW,
  pidoi: AI_PLATFORM_PIDOI,
  qigi: AI_PLATFORM_PIDOI,
  qiqi: AI_PLATFORM_PIDOI,
}

export class MissingCatalogPlatformError extends Error {
  constructor(context?: string) {
    super(
      context
        ? `未知 AI 平台标识（${context}），请检查模型目录或 platform 配置`
        : '未知 AI 平台标识，请检查模型目录或 platform 配置',
    )
    this.name = 'MissingCatalogPlatformError'
  }
}

/** 归一化 platform-tag 或 platform-key 为小写 platform-key */
export function normalizeCatalogPlatformId(platform: string): AIPlatformId | null {
  const trimmed = platform.trim()
  if (!trimmed) return null
  const lower = trimmed.toLowerCase()
  const fromKey = PLATFORM_BY_LOWER[lower]
  if (fromKey) return fromKey
  const tagKey = trimmed.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  return PLATFORM_TAG_ALIASES[tagKey] ?? null
}

export function requireCatalogPlatformId(platform: string | undefined | null, context?: string): string {
  if (typeof platform === 'string') {
    const normalized = normalizeCatalogPlatformId(platform)
    if (normalized) return normalized
  }
  throw new MissingCatalogPlatformError(context)
}

/** 是否为 OpenAI {@code /v1/images/generations} 兼容生图平台（诗云） */
export function isOpenAiCompatibleImagePlatform(platformId: string): boolean {
  const normalized = normalizeCatalogPlatformId(platformId)
  return normalized != null && OPENAI_COMPATIBLE_IMAGE_PLATFORMS.has(normalized)
}
